import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PORTAL, usePortal } from '../portalAuth';
import { IMG } from '../components/StoreLayout';
import { STORE_BASE } from '../routeBase';
import { ProductCard } from './Home';

const STATUS_LABEL = {
  processing: 'Processing', packing: 'Packing', ready: 'Ready for dispatch',
  dispatched: 'Dispatched', in_transit: 'In transit', delivered: 'Delivered',
  completed: 'Completed', cancelled: 'Cancelled',
};
const STATUS_COLOR = {
  processing: '#64748b', packing: '#9a6a00', ready: '#1e40af', dispatched: '#7c3aed',
  in_transit: '#7c3aed', delivered: '#166534', completed: '#166534', cancelled: '#991b1b',
};

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; }
function money(n, cur) { return `${cur || ''} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim(); }

export default function Dashboard() {
  const { customer, ready } = usePortal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [requesting, setRequesting] = useState(false);

  const loadDashboard = useCallback(() => {
    PORTAL.get('/dashboard').then(r => {
      setData(r.data);
      const client = PORTAL;
      client.get('/products', { params: { limit: 4 } }).then(rr => setNewArrivals(rr.data.products || []));
      client.get('/products', { params: { limit: 4, category: r.data.recommended_category || undefined } })
        .then(rr => setRecommended(rr.data.products || []));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (customer) loadDashboard(); }, [customer, loadDashboard]);
  useEffect(() => {
    if (customer) PORTAL.get('/notifications').then(r => setNotifications(r.data.notifications || []));
  }, [customer]);
  useEffect(() => {
    if (!customer) return;
    PORTAL.get('/quotations').then(r => setQuotations(r.data.quotations || []));
    PORTAL.get('/credit-notes').then(r => setCreditNotes(r.data.credit_notes || []));
  }, [customer]);

  const requestStatement = async () => {
    setRequesting(true);
    try {
      const r = await PORTAL.post('/request-document', { doc_type: 'statement' });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not send request'); }
    setRequesting(false);
  };

  const markAllRead = async () => {
    await PORTAL.put('/notifications/read-all');
    setNotifications(list => list.map(n => ({ ...n, is_read: 1 })));
    setData(d => d ? { ...d, unread_notifications: 0 } : d);
  };
  const markRead = async (n) => {
    if (n.is_read) return;
    await PORTAL.put(`/notifications/${n.id}/read`);
    setNotifications(list => list.map(x => x.id === n.id ? { ...x, is_read: 1 } : x));
    setData(d => d ? { ...d, unread_notifications: Math.max(0, d.unread_notifications - 1) } : d);
  };

  if (!ready || loading) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Please sign in to view your dashboard</h2>
      <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)' }}>Sign in</Link>
    </div>
  );
  if (!data) return null;

  const { outstanding, outstanding_invoices, orders, shipments, rep, entity } = data;

  return (
    <div style={{ maxWidth: 1200, margin: '24px auto', padding: '0 24px 60px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Welcome back, {customer.company}</h1>
          <p style={{ fontSize: 13, color: '#888', margin: '4px 0 0' }}>Here's what's happening with your account.</p>
        </div>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowNotifs(s => !s)} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 8,
            border: '1.5px solid #e5e5e5', background: '#fff', cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
          }}>
            <i className="ti ti-bell" style={{ fontSize: 16 }} />
            Notifications
            {data.unread_notifications > 0 && (
              <span style={{ background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>
                {data.unread_notifications}
              </span>
            )}
          </button>
          {showNotifs && (
            <div style={{ position: 'absolute', right: 0, top: '110%', width: 340, background: '#fff', border: '1px solid #eee',
              borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.12)', zIndex: 50, maxHeight: 400, overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #f2f2f2' }}>
                <strong style={{ fontSize: 13 }}>Notifications</strong>
                {notifications.some(n => !n.is_read) && (
                  <button onClick={markAllRead} style={{ fontSize: 11.5, color: 'var(--sf-accent)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>No notifications yet.</div>}
              {notifications.map(n => (
                <div key={n.id} onClick={() => markRead(n)} style={{
                  padding: '10px 14px', borderBottom: '1px solid #f7f7f7', cursor: 'pointer',
                  background: n.is_read ? '#fff' : '#f0f9ff',
                }}>
                  {/* Admin broadcast campaigns (Notification Manager) can carry a
                      banner image — a routine system notification never has one. */}
                  {n.image_url && (
                    <img src={n.image_url.startsWith('/') ? n.image_url : `/product-images/${n.image_url}`}
                      alt="" style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
                  )}
                  <div style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{n.message}</div>
                  <div style={{ fontSize: 10.5, color: '#aaa', marginTop: 3 }}>{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* STAT CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard label="Outstanding Balance" value={money(outstanding.total, outstanding.currency)} color="#dc2626" icon="file-invoice" />
        <StatCard label="Available Credit" value={outstanding.available_credit != null ? money(outstanding.available_credit, outstanding.currency) : '—'} color="#16a34a" icon="credit-card" />
        <StatCard label="Pending Orders" value={orders.pending_count} color="#7c3aed" icon="clock" />
        <StatCard label="Lifetime Orders" value={orders.total_count} color="var(--sf-primary)" icon="shopping-bag" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, alignItems: 'start' }}>
        {/* LEFT COLUMN */}
        <div>
          {/* SHIPMENTS / TRACKING */}
          {shipments.length > 0 && (
            <Section title="Shipment Tracking">
              {shipments.map(s => (
                <div key={s.id} style={{ padding: '12px 0', borderTop: '1px solid #f2f2f2' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Link to={`${STORE_BASE}/orders/${s.id}`} style={{ fontWeight: 600, fontSize: 13.5, color: 'inherit', textDecoration: 'none' }}>{s.order_number}</Link>
                    <StatusBadge status={s.status} />
                  </div>
                  <div style={{ fontSize: 12.5, color: '#666', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                    {s.awb_bl_number && <span><strong>AWB/BL:</strong> {s.awb_bl_number}</span>}
                    {s.dispatch_date && <span><strong>Dispatched:</strong> {fmtDate(s.dispatch_date)}</span>}
                    {s.eta_date && <span><strong>ETA:</strong> {fmtDate(s.eta_date)}</span>}
                  </div>
                  {s.tracking_url ? (
                    <a href={s.tracking_url} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--sf-accent)', display: 'inline-block', marginTop: 6 }}>
                      📍 Track shipment →
                    </a>
                  ) : s.awb_bl_number && (
                    <a href={`https://www.google.com/search?q=${encodeURIComponent(s.awb_bl_number + ' tracking')}`} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12.5, color: 'var(--sf-accent)', display: 'inline-block', marginTop: 6 }}>
                      🔍 Search tracking →
                    </a>
                  )}
                </div>
              ))}
            </Section>
          )}

          {/* OUTSTANDING INVOICES */}
          {outstanding_invoices.length > 0 && (
            <Section title="Outstanding Invoices">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#999', fontSize: 11.5 }}>
                    <th style={{ padding: '6px 4px' }}>Invoice</th>
                    <th style={{ padding: '6px 4px' }}>Due</th>
                    <th style={{ padding: '6px 4px', textAlign: 'right' }}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding_invoices.map(inv => (
                    <tr key={inv.id} style={{ borderTop: '1px solid #f2f2f2' }}>
                      <td style={{ padding: '8px 4px' }}>{inv.invoice_no}</td>
                      <td style={{ padding: '8px 4px', color: '#777' }}>{fmtDate(inv.due_date)}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>
                        {money(inv.amount_outstanding, inv.currency_code)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 10 }}>
                <button onClick={requestStatement} disabled={requesting} style={{
                  fontSize: 12, color: 'var(--sf-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                }}>
                  {requesting ? 'Sending…' : '📄 Request Account Statement'}
                </button>
              </div>
            </Section>
          )}

          {/* SAVED QUOTES */}
          {quotations.length > 0 && (
            <Section title="Saved Quotes">
              {quotations.map(q => (
                <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderTop: '1px solid #f2f2f2' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{q.quote_number}</div>
                    <div style={{ fontSize: 11.5, color: '#888' }}>{fmtDate(q.created_at)} · {money(q.total, q.currency_code)}</div>
                  </div>
                  <a href={`/api/portal/quotations/${q.id}/pdf?token=${encodeURIComponent(localStorage.getItem('sf_customer_token') || '')}`}
                    target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--sf-accent)', textDecoration: 'none' }}>
                    View / Print →
                  </a>
                </div>
              ))}
            </Section>
          )}

          {/* CREDIT NOTES */}
          {creditNotes.length > 0 && (
            <Section title="Credit Notes">
              {creditNotes.map(cn => (
                <div key={cn.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderTop: '1px solid #f2f2f2' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{cn.reference_number}</div>
                    <div style={{ fontSize: 11.5, color: '#888' }}>{fmtDate(cn.transaction_date)}{cn.description ? ` · ${cn.description}` : ''}</div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#166534' }}>{money(cn.credit, cn.currency_code)}</div>
                </div>
              ))}
            </Section>
          )}

          {/* PENDING ORDERS */}
          <Section title="Pending Orders" empty={orders.pending.length === 0} emptyText="No pending orders right now.">
            {orders.pending.map(o => <OrderRow key={o.id} o={o} currency={outstanding.currency} />)}
          </Section>

          {/* PREVIOUS ORDERS */}
          <Section title="Previous Orders" empty={orders.recent.length === 0} emptyText="No orders yet.">
            {orders.recent.map(o => <OrderRow key={o.id} o={o} currency={outstanding.currency} />)}
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <Link to={`${STORE_BASE}/orders`} style={{ fontSize: 13, color: 'var(--sf-accent)', textDecoration: 'none' }}>View all orders →</Link>
            </div>
          </Section>
        </div>

        {/* RIGHT COLUMN */}
        <div>
          {rep && (
            <Section title="Your Sales Representative">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--sf-accent, #85cde9)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                  {rep.name?.[0]?.toUpperCase() || 'S'}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{rep.name}</div>
                  {rep.phone && (
                    <a href={`https://wa.me/${rep.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#25D366', textDecoration: 'none', marginTop: 2 }}>
                      <i className="ti ti-brand-whatsapp" style={{ fontSize: 14 }} /> {rep.phone}
                    </a>
                  )}
                  {rep.email && (
                    <a href={`mailto:${rep.email}`} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--sf-accent)', textDecoration: 'none', marginTop: 2 }}>
                      <i className="ti ti-mail" style={{ fontSize: 14 }} /> {rep.email}
                    </a>
                  )}
                </div>
              </div>
            </Section>
          )}

          {entity && (
            <Section title="Company Details">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                {entity.country_code ? (
                  <img src={`https://flagcdn.com/h20/${entity.country_code.toLowerCase()}.png`} alt={entity.country} style={{ height: 14, borderRadius: 2 }} />
                ) : (entity.flag_emoji ? `${entity.flag_emoji} ` : '')}
                {entity.name}
              </div>
              {(entity.address || entity.city || entity.country) && (
                <div style={{ fontSize: 12.5, color: '#666', marginBottom: 4 }}>
                  {[entity.address, entity.city, entity.country].filter(Boolean).join(', ')}
                </div>
              )}
              {entity.phone && (
                <a href={`https://wa.me/${entity.phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#25D366', textDecoration: 'none', marginTop: 2 }}>
                  <i className="ti ti-brand-whatsapp" style={{ fontSize: 14 }} /> {entity.phone}
                </a>
              )}
              {entity.email && (
                <a href={`mailto:${entity.email}`} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--sf-accent)', textDecoration: 'none', marginTop: 2 }}>
                  <i className="ti ti-mail" style={{ fontSize: 14 }} /> {entity.email}
                </a>
              )}
              {entity.website && (
                <a href={entity.website.startsWith('http') ? entity.website : `https://${entity.website}`} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--sf-accent)', textDecoration: 'none', marginTop: 2 }}>
                  <i className="ti ti-world" style={{ fontSize: 14 }} /> {entity.website}
                </a>
              )}
            </Section>
          )}

          <Section title="Your Wishlist">
            <WishlistGlance />
          </Section>
        </div>
      </div>

      {/* RECOMMENDED PRODUCTS */}
      {recommended.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Recommended For You</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {recommended.map(p => <ProductCard key={p.model_no} p={p} />)}
          </div>
        </section>
      )}

      {/* NEW ARRIVALS */}
      {newArrivals.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>New Arrivals</h2>
            <Link to={`${STORE_BASE}/shop`} style={{ fontSize: 13, color: 'var(--sf-accent)', textDecoration: 'none' }}>View all new arrivals →</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {newArrivals.map(p => <ProductCard key={p.model_no} p={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`ti ti-${icon}`} style={{ fontSize: 16, color }} />
        </div>
        <span style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

function Section({ title, children, empty, emptyText }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>{title}</h3>
      {empty ? <div style={{ fontSize: 13, color: '#999', padding: '10px 0' }}>{emptyText}</div> : children}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
      background: `${STATUS_COLOR[status] || '#666'}18`, color: STATUS_COLOR[status] || '#666' }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function OrderRow({ o, currency }) {
  return (
    <Link to={`${STORE_BASE}/orders/${o.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
      borderTop: '1px solid #f2f2f2', flexWrap: 'wrap', textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{o.order_number}</div>
        <div style={{ fontSize: 11.5, color: '#888' }}>{fmtDate(o.created_at)} · {o.items_count} item(s)</div>
      </div>
      <StatusBadge status={o.status} />
      <div style={{ fontWeight: 700, fontSize: 13, minWidth: 80, textAlign: 'right' }}>{money(o.total_amount, currency)}</div>
    </Link>
  );
}

function WishlistGlance() {
  const portal = usePortal();
  const wishlist = portal?.wishlist;
  if (!wishlist || wishlist.count === 0) {
    return <div style={{ fontSize: 13, color: '#999' }}>Your wishlist is empty. <Link to={`${STORE_BASE}/shop`} style={{ color: 'var(--sf-accent)' }}>Browse products →</Link></div>;
  }
  return (
    <div>
      <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>{wishlist.count} item(s) saved</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(wishlist.items || []).slice(0, 4).map(it => (
          <Link key={it.variant_id} to={`${STORE_BASE}/product/${encodeURIComponent(it.model_no)}`} style={{ display: 'block' }}>
            <img src={IMG(it.image)} alt="" loading="lazy" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }} />
          </Link>
        ))}
      </div>
      <Link to={`${STORE_BASE}/wishlist`} style={{ fontSize: 12.5, color: 'var(--sf-accent)', textDecoration: 'none', display: 'inline-block', marginTop: 10 }}>
        View full wishlist →
      </Link>
    </div>
  );
}
